import { forwardRef, useLayoutEffect, useRef, type TextareaHTMLAttributes } from "react";

const DEFAULT_MAX_HEIGHT = 180;

export interface AutoGrowTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  maxHeight?: number;
}

function resizeTextarea(textarea: HTMLTextAreaElement, maxHeight: number) {
  textarea.style.height = "auto";
  const contentHeight = textarea.scrollHeight;
  textarea.style.height = contentHeight > 0 ? `${Math.min(contentHeight, maxHeight)}px` : "auto";
  textarea.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

export const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  function AutoGrowTextarea(
    { maxHeight = DEFAULT_MAX_HEIGHT, onChange, value, ...props },
    forwardedRef,
  ) {
    const localRef = useRef<HTMLTextAreaElement | null>(null);

    const assignRef = (node: HTMLTextAreaElement | null) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };

    useLayoutEffect(() => {
      if (localRef.current) resizeTextarea(localRef.current, maxHeight);
    }, [maxHeight, value]);

    return (
      <textarea
        {...props}
        ref={assignRef}
        value={value}
        rows={1}
        onChange={(event) => {
          resizeTextarea(event.currentTarget, maxHeight);
          onChange?.(event);
        }}
      />
    );
  },
);
